import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Handler from "../../Helpers/Handler";
import { formatDateTime } from "../../Helpers/Utils";
import { StatusPill } from "../../Components/Needed";
import ListLayout from "../../Components/List";
import RoleFormModal from "./RoleFormModal";
import useListParams from "../../Helpers/useListParams";

const INITIAL_FILTERS = { search: "", searchKey: "roleName", count: 10, status: "", departmentId: "", sortBy: "createdAt", sort: false };

const SEARCH_KEYS = [{ key: "roleName", value: "Role name" }];

const RoleList = () => {
    const navigate = useNavigate();
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilters } = useListParams(INITIAL_FILTERS);
    const [data, setData] = useState({ list: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);

    // Active departments feed both the "Department" filter dropdown below and
    // the create/edit form's department picker + its per-department task
    // catalog - fetched once here and handed down rather than re-fetched.
    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "GET", url: "/admin/staff/department/active" });
            setDepartments(response?.success ? response?.data?.data || [] : []);
        })();
    }, []);

    const filterItems = useMemo(() => ({
        dropdown: [
            {
                key: "status",
                name: "Status",
                options: [{ key: "", name: "All" }, { key: true, name: "Active" }, { key: false, name: "Inactive" }],
            },
            {
                key: "departmentId",
                name: "Department",
                options: [{ key: "", name: "All" }, ...departments.map((d) => ({ key: d.departmentId, name: d.departmentName }))],
            },
        ],
        sort: [
            { key: "roleName", name: "Name" },
            { key: "createdAt", name: "Created" },
        ],
    }), [departments]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        const response = await Handler({
            method: "post",
            url: `/admin/staff/role/list/${page - 1}`,
            params: { count: filters.count },
            data: {
                ...(filters.search ? { search: filters.search } : {}),
                ...(filters.status !== "" ? { status: filters.status } : {}),
                ...(filters.departmentId ? { departmentId: filters.departmentId } : {}),
            },
        });
        if (response?.success) {
            setData(response?.data?.data || { list: [], total: 0 });
        } else {
            setData({ list: [], total: 0 });
            setError(response?.data?.message || "No data found");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, filters.count, filters.search, filters.status, filters.departmentId]);

    useEffect(() => { load(); }, [load]);

    const onFilterChange = (key, value) => {
        if (key !== "count") setPage(1);
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resetFilter = () => {
        setFilters(INITIAL_FILTERS);
        setPage(1);
    };

    const columns = [
        { key: "roleId", name: "Role ID", mandatory: true },
        { key: "roleName", name: "Name" },
        { key: "departmentName", name: "Department", render: (row) => row.departmentName || <span className="text-muted">-</span> },
        { key: "status", name: "Status", render: (row) => <StatusPill active={row.status} /> },
        { key: "createdAt", name: "Created", render: (row) => formatDateTime(row.createdAt) },
    ];

    return (
        <>
            <ListLayout
                title="Roles"
                description="Permission sets staff members are assigned within a department."
                data={data}
                loading={loading}
                error={error}
                onRetry={load}
                onRefresh={load}
                table={columns}
                page={page}
                setPageNo={setPage}
                pageSize={filters.count}
                onPageSizeChange={(size) => onFilterChange("count", size)}
                filters={filters}
                initialFilters={INITIAL_FILTERS}
                filterItems={filterItems}
                onFilterChange={onFilterChange}
                resetFilter={resetFilter}
                searchKeys={SEARCH_KEYS}
                searchPlaceholder="Search by role name..."
                onRowClick={(row) => navigate(`/roles/${row.roleId}`)}
                addList={departments.length ? () => setShowAddModal(true) : undefined}
            />

            {showAddModal ? (
                <RoleFormModal
                    mode="create"
                    departments={departments}
                    onClose={() => setShowAddModal(false)}
                    onSaved={() => { setShowAddModal(false); load(); }}
                />
            ) : ""}
        </>
    );
};

export default RoleList;
